import type { OptionType } from "components/inputs";
import { DatePicker, MaskedInput, Select, TextArea } from "components/inputs";
import { format } from "date-fns";
import { useState, useCallback, useEffect } from "react";
import {
  usePatchMSAReReviewMutation,
  useDeleteMSAReReviewMutation,
} from "services/msa-api";
import { STRING_TO_DATE } from "utils/date-utils";
import { useSelector } from "utils/redux";
import { KaboodleToaster } from "components/common";
import ButtonIcon from "components/ButtonIcon/ButtonIcon";
import Button from "components/Button/Button";
import Card from "components/Card/Card";
import KaboodleModal from "components/Modals/KaboodleModal";
import type { MSAReReviewType } from "types/DataTypes";

type MSAReReviewProps = {
  review: MSAReReviewType;
};

const MSAReReview = ({ review }: MSAReReviewProps) => {
  const constants = useSelector((state) => state.shared.constants);
  const [patchMSAReReview] = usePatchMSAReReviewMutation();
  const [deleteMSAReReview, { isLoading: isDeleting }] =
    useDeleteMSAReReviewMutation();

  const [reviewToDelete, setReviewToDelete] = useState<number | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [description, setDescription] = useState<string | null>(
    review.description || "",
  );
  const [amountValue, setAmountValue] = useState<string | number | null>(
    review.amount_of_change_requested || "",
  );

  useEffect(() => {
    setDescription(review.description || "");
    setAmountValue(review.amount_of_change_requested || "");
  }, [review]);

  const handleSave = useCallback(
    async (reviewId: string, field: string, value: unknown) => {
      try {
        await patchMSAReReview({
          reviewId,
          data: { [field]: value },
        }).unwrap();

        KaboodleToaster.success("Saved Successfully");
      } catch (error) {
        KaboodleToaster.error(
          `Failed to update field: ${error.data?.[field]?.[0] || "Unknown error"}`,
        );
      }
    },
    [patchMSAReReview],
  );

  const handleDescriptionChange = (
    value: string | React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const newValue = typeof value === "string" ? value : value.target.value;
    setDescription(newValue === "" ? null : newValue);
  };

  const handleNumericSave = useCallback(
    async (reviewId: string, field: string, value: string | number | null) => {
      const processedValue =
        typeof value === "string" ? value.replace("$", "") : value;

      try {
        await patchMSAReReview({
          reviewId,
          data: { [field]: processedValue },
        }).unwrap();

        KaboodleToaster.success("Saved Successfully");
      } catch (error) {
        KaboodleToaster.error(
          `Failed to update field: ${error.data?.[field]?.[0] || "Unknown error"}`,
        );
      }
    },
    [patchMSAReReview],
  );

  const handleDelete = async () => {
    if (!reviewToDelete) return;

    try {
      await deleteMSAReReview({
        reviewId: reviewToDelete.toString(),
      }).unwrap();

      setShowDeleteConfirm(false);
      setReviewToDelete(null);
      KaboodleToaster.success("Re-Review deleted successfully");
    } catch (error) {
      KaboodleToaster.error(
        error?.data?.detail || "Failed to delete re-review",
      );
    }
  };

  const openDeleteModal = (reviewId: number) => {
    setReviewToDelete(reviewId);
    setShowDeleteConfirm(true);
  };

  return (
    <>
      <KaboodleModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        footer={
          <div className="flex jc-c">
            <Button
              className="delete"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              Confirm Delete
            </Button>
            <Button onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
          </div>
        }
        children={
          <Card heading="Delete Re-Review">
            <p>Are you sure you want to delete this re-review?</p>
          </Card>
        }
      />
      <div className="data-block max-content-w">
        <div className="data-block-header flex jc-sb">
          <div>Re-Review {review.category ? `(${review.category})` : ""}</div>
          {review.deletable && (
            <ButtonIcon
              size="small"
              onClick={() => openDeleteModal(review.id)}
              title="Delete Re-Review"
            >
              delete
            </ButtonIcon>
          )}
        </div>
        <div className="data-values">
          <label>Date Requested:</label>
          <DatePicker
            name={`review-${review.id}-date-requested`}
            onDateChange={(date) =>
              handleSave(
                review.id.toString(),
                "date_requested",
                format(date, "yyyy-MM-dd"),
              )
            }
            initialDate={
              review.date_requested
                ? STRING_TO_DATE(review.date_requested)
                : undefined
            }
          />

          <label>Date Delivered to Client:</label>
          <DatePicker
            name={`review-${review.id}-date-delivered`}
            onDateChange={(date) =>
              handleSave(
                review.id.toString(),
                "date_delivered_to_client",
                format(date, "yyyy-MM-dd"),
              )
            }
            initialDate={
              review.date_delivered_to_client
                ? STRING_TO_DATE(review.date_delivered_to_client)
                : undefined
            }
          />

          <label>Category:</label>
          <Select
            name={`review-${review.id}-category`}
            value={
              review.category
                ? constants?.MSA_RE_REVIEW_CATEGORY?.find(
                    (option) => option.value === review.category,
                  ) || null
                : null
            }
            onChange={(selectedOption: OptionType) => {
              handleSave(
                review.id.toString(),
                "category",
                selectedOption ? selectedOption.value : null,
              );
            }}
            options={constants?.MSA_RE_REVIEW_CATEGORY || []}
            placeholder="Select category"
          />

          <label>Amount of Change Requested:</label>
          <MaskedInput
            name={`review-${review.id}-amount-change`}
            mask="dollar"
            value={amountValue || ""}
            onChange={(value) => {
              setAmountValue(
                value.target.value === "" ? null : value.target.value,
              );
            }}
            onBlur={() => {
              handleNumericSave(
                review.id.toString(),
                "amount_of_change_requested",
                amountValue,
              );
            }}
          />

          <label>Description:</label>
          <TextArea
            name={`review-${review.id}-description`}
            label=""
            value={description || ""}
            advancedEdit={true}
            onSave={async (value) => {
              handleDescriptionChange(value);
              await handleSave(review.id.toString(), "description", value);
            }}
            placeholder="Enter description"
          />
        </div>
      </div>
    </>
  );
};

export default MSAReReview;
